import * as THREE from 'three';
import { HealthSystem } from './HealthSystem';

/**
 * System for displaying 3D healthbars above entities
 */
export class HealthbarSystem {
  private object: THREE.Object3D;
  private healthSystem: HealthSystem;
  private barWidth: number = 1.0;
  private barHeight: number = 0.15; // Increased height for better visibility
  private barDepth: number = 0.03; // Reduced depth for a flatter look
  private barOffset: number = 0.6; // Offset above the entity
  private barBackground: THREE.Mesh;
  private barFill: THREE.Mesh;
  private isVisible: boolean = true;
  private entityHeight: number;
  private parent: THREE.Object3D;
  
  /**
   * Creates a new healthbar system
   * @param healthSystem The health system to visualize
   * @param parent The parent object to attach the healthbar to
   * @param entityHeight The height of the entity (to position the bar)
   */
  constructor(healthSystem: HealthSystem, parent: THREE.Object3D, entityHeight: number) {
    this.healthSystem = healthSystem;
    this.entityHeight = entityHeight;
    this.parent = parent;
    
    // Create container object
    this.object = new THREE.Object3D();
    
    // Position above the entity
    this.object.position.y = entityHeight + this.barOffset;
    
    // Create background bar (black/dark gray)
    const backgroundGeometry = new THREE.BoxGeometry(this.barWidth, this.barHeight, this.barDepth);
    const backgroundMaterial = new THREE.MeshBasicMaterial({ 
      color: 0x222222,
      depthTest: true,
      transparent: false
    });
    this.barBackground = new THREE.Mesh(backgroundGeometry, backgroundMaterial);
    this.barBackground.renderOrder = 1;
    this.object.add(this.barBackground);
    
    // Create health fill bar (green)
    // Make the fill bar slightly smaller than the background for a border effect
    const fillGeometry = new THREE.BoxGeometry(this.barWidth * 0.98, this.barHeight * 0.9, this.barDepth);
    const fillMaterial = new THREE.MeshBasicMaterial({ 
      color: 0x00ff00,
      depthTest: true,
      transparent: false
    });
    this.barFill = new THREE.Mesh(fillGeometry, fillMaterial);
    
    // Position the fill bar slightly in front of the background to prevent z-fighting
    this.barFill.position.z = 0.01;
    this.barFill.renderOrder = 2;
    
    this.object.add(this.barFill);
    
    // Add to parent
    parent.add(this.object);
    
    // Update the bar initially
    this.update();
    
    // Add callback to update when health changes
    this.healthSystem.onDamage(() => this.update());
    this.healthSystem.onHeal(() => this.update());
  }
  
  /**
   * Updates the healthbar to match the current health
   */
  public update(): void {
    const healthPercentage = this.healthSystem.getHealthPercentage();
    
    // Update the fill bar scale and position
    this.barFill.scale.x = healthPercentage;
    
    // Adjust position so it scales from the left
    // Account for the slightly smaller size of the fill bar
    this.barFill.position.x = (this.barWidth * (healthPercentage - 1) * 0.98) / 2;
    
    // Update color based on health percentage
    const fillMaterial = this.barFill.material as THREE.MeshBasicMaterial;
    
    if (healthPercentage > 0.6) {
      fillMaterial.color.setHex(0x00ff00); // Green
    } else if (healthPercentage > 0.3) {
      fillMaterial.color.setHex(0xffff00); // Yellow
    } else {
      fillMaterial.color.setHex(0xff0000); // Red
    }
    
    // Hide if dead
    if (this.healthSystem.isDying()) {
      this.hide();
    }
  }
  
  /**
   * Makes the healthbar face the camera
   * @param camera The camera to face
   */
  public faceCamera(camera: THREE.Camera): void {
    if (!this.isVisible) return;
    
    // First, cancel out any rotation from the parent by applying the inverse of parent's quaternion
    // This effectively resets our rotation to world space
    const parentWorldQuaternion = new THREE.Quaternion();
    this.parent.getWorldQuaternion(parentWorldQuaternion);
    const inverseParentQuaternion = parentWorldQuaternion.clone().invert();
    this.object.quaternion.copy(inverseParentQuaternion);
    
    // Then apply the camera's quaternion to face the camera directly
    this.object.quaternion.multiply(camera.quaternion);
  }
  
  /**
   * Shows the healthbar
   */
  public show(): void {
    this.object.visible = true;
    this.isVisible = true;
  }
  
  /**
   * Hides the healthbar
   */
  public hide(): void {
    this.object.visible = false;
    this.isVisible = false;
  }
  
  /**
   * Disposes of the healthbar resources
   */
  public dispose(): void {
    // Remove from parent
    if (this.object.parent) {
      this.object.parent.remove(this.object);
    }
    
    // Dispose of geometries and materials
    (this.barBackground.geometry as THREE.BufferGeometry).dispose();
    (this.barBackground.material as THREE.Material).dispose();
    (this.barFill.geometry as THREE.BufferGeometry).dispose();
    (this.barFill.material as THREE.Material).dispose();
  }

  /**
   * Sets a custom offset for the healthbar
   * @param offset The offset vector to apply
   */
  public setOffset(offset: THREE.Vector3): void {
    this.object.position.copy(offset);
  }
} 